
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  date: string;
  author: string;
  slug: string;
  published: boolean;
  featured: boolean;
}

export const useBlogPosts = () => {
  return useQuery({
    queryKey: ["blog-posts"],
    queryFn: async () => {
      console.log("Fetching blog posts from database...");
      
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("published", true)
        .order("published_at", { ascending: false });

      if (error) {
        console.error("Error fetching blog posts:", error);
        throw error;
      }

      // Transform the data to match the expected interface
      const transformedData: BlogPost[] = data.map(post => ({
        id: post.id,
        title: post.title,
        excerpt: post.excerpt,
        content: post.content,
        date: post.published_at,
        author: post.author,
        slug: post.slug,
        published: post.published,
        featured: post.featured
      }));

      console.log("Blog posts fetched successfully:", transformedData);
      return transformedData;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

export const useBlogPost = (slug: string) => {
  return useQuery({
    queryKey: ["blog-post", slug],
    queryFn: async () => {
      console.log("Fetching blog post by slug:", slug);
      
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .eq("published", true)
        .maybeSingle();

      if (error) {
        console.error("Error fetching blog post:", error);
        throw error;
      }

      if (!data) {
        return null;
      }

      // Transform the data to match the expected interface
      const transformedData: BlogPost = {
        id: data.id,
        title: data.title,
        excerpt: data.excerpt,
        content: data.content,
        date: data.published_at,
        author: data.author,
        slug: data.slug,
        published: data.published,
        featured: data.featured
      };

      console.log("Blog post fetched successfully:", transformedData);
      return transformedData;
    },
    enabled: !!slug,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

export const useFeaturedBlogPost = () => {
  return useQuery({
    queryKey: ["featured-blog-post"],
    queryFn: async () => {
      console.log("Fetching featured blog post...");
      
      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("published", true)
        .eq("featured", true)
        .order("published_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error("Error fetching featured blog post:", error);
        throw error;
      }

      if (!data) {
        // If no featured post, get the most recent one
        const { data: recentData, error: recentError } = await supabase
          .from("blog_posts")
          .select("*")
          .eq("published", true)
          .order("published_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (recentError) {
          console.error("Error fetching recent blog post:", recentError);
          throw recentError;
        }

        if (!recentData) {
          return null;
        }

        return {
          id: recentData.id,
          title: recentData.title,
          excerpt: recentData.excerpt,
          content: recentData.content,
          date: recentData.published_at,
          author: recentData.author,
          slug: recentData.slug,
          published: recentData.published,
          featured: recentData.featured
        };
      }

      // Transform the data to match the expected interface
      const transformedData: BlogPost = {
        id: data.id,
        title: data.title,
        excerpt: data.excerpt,
        content: data.content,
        date: data.published_at,
        author: data.author,
        slug: data.slug,
        published: data.published,
        featured: data.featured
      };

      console.log("Featured blog post fetched successfully:", transformedData);
      return transformedData;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
  });
};
